import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useCartStore, CartItem } from '../store/cartStore';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import { formatDateInTimezone, getBrowserTimezone } from '../utils/timezone';
import { getGuestContact } from '../services/checkout/guestCheckoutSession';
import { GuestAccountHint } from '../components/GuestAccountPrompt';

const CheckoutPage: React.FC = () => {
  const auth = useAuth();
  const { cart, fetchCart } = useCartStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Filter for PayPal items only
  const paypalItems = cart?.items.filter(item => item.listingId.paymentMethod === 'paypal') || [];

  useEffect(() => {
    if (!cart || cart.items.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
      return;
    }

    if (paypalItems.length === 0) {
      toast.error('No platform listings in cart');
      navigate('/cart');
      return;
    }

    // Load PayPal SDK
    const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test';
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=USD`;
    script.async = true;
    script.onload = () => setShowPayPal(true);
    script.onerror = () => {
      toast.error('Failed to load PayPal');
    };
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector(
        `script[src*="paypal.com/sdk/js"]`
      );
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, [cart, navigate, paypalItems.length]);

  const renderPayPalButton = useCallback(() => {
    if (!window.paypal || !cart) return;

    const buttonContainer = document.getElementById('paypal-button-container');
    if (!buttonContainer || buttonContainer.children.length > 0) return;

    // A guest's contact info was already required to reach the server-side
    // guest cart at CartPage.tsx; re-sending it here lets the backend create
    // Rentals with renterName/renterEmail even though renterId stays unset.
    const guestContact = auth.isAuthenticated ? undefined : getGuestContact() || undefined;

    window.paypal
      .Buttons({
        createOrder: async () => {
          try {
            const { orderId } = await api.post<{ orderId: string }>(
              '/payments/paypal/create-order',
              guestContact ? { guestContact } : {}
            );
            return orderId;
          } catch (error) {
            toast.error('Failed to create order');
            throw error;
          }
        },
        onApprove: async (data: { orderID: string }) => {
          setIsProcessing(true);
          setPaymentError(null);
          try {
            const response = await api.post<{
              success: boolean;
              rentals: unknown[];
              emailStatus?: {
                sent: number;
                failed: number;
                failedRentals: string[];
              };
              guestAccountHint?: GuestAccountHint;
            }>('/payments/paypal/capture-order', {
              orderId: data.orderID,
              ...(guestContact ? { guestContact } : {}),
            });

            if (response.success) {
              // The backend removes only the PayPal items it just charged for.
              // Re-fetch rather than clearing, or Stripe items the buyer still
              // has to pay for would disappear from their cart.
              await fetchCart();
              navigate(`/checkout/success?orderId=${data.orderID}`, {
                state: {
                  emailStatus: response.emailStatus,
                  rentalCount: response.rentals?.length || 0,
                  // Guests can't call the authenticated /rentals/my-rentals
                  // endpoint, so PostCheckoutPage reads the rentals the
                  // capture just created directly from here instead.
                  rentals: response.rentals,
                  guestAccountHint: response.guestAccountHint,
                },
              });
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Payment processing failed. Please try again.';
            setPaymentError(errorMessage);
            setIsProcessing(false);
          }
        },
        onError: (err: Error) => {
          console.error('PayPal error:', err);
          const errorMessage = err.message || 'Payment failed. Please try again.';
          setPaymentError(errorMessage);
          setIsProcessing(false);
        },
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay',
        },
      })
      .render('#paypal-button-container');
  }, [cart, fetchCart, navigate, auth.isAuthenticated]);

  useEffect(() => {
    if (showPayPal && window.paypal) {
      renderPayPalButton();
    }
  }, [showPayPal, renderPayPalButton]);

  if (!cart || cart.items.length === 0) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Calculate total for PayPal items only
  const total = paypalItems.reduce((sum, item) => sum + item.price, 0);

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Checkout</h1>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Order Summary (Platform Listings)
            </h2>
            <div className="space-y-4">
              {paypalItems.map((item: CartItem, index: number) => {
                const timezone = item.listingId?.timezone || getBrowserTimezone();

                return (
                <div key={index} className="border-b pb-4 last:border-b-0">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {item.listingId?.title || 'Rental Item'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {formatDateInTimezone(new Date(item.startDateTime), timezone)}{' '}
                    -{' '}
                    {formatDateInTimezone(new Date(item.endDateTime), timezone)}
                  </p>
                  {item.renterDetails && Object.keys(item.renterDetails).length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      {Object.entries(item.renterDetails).map(
                        ([key, value]) =>
                          value && (
                            <div key={key}>
                              <span className="font-medium">{key}:</span> {value}
                            </div>
                          )
                      )}
                    </div>
                  )}
                  <p className="text-lg font-semibold text-gray-900 mt-2">
                    ${item.price.toFixed(2)}
                  </p>
                </div>
                );
              })}
            </div>

            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-gray-900">Total:</span>
                <span className="text-2xl font-bold text-primary-600">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <Button
            variant="secondary"
            onClick={() => navigate('/cart')}
            className="w-full"
            disabled={isProcessing}
          >
            Back to Cart
          </Button>
        </div>

        <div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment</h2>

            {paymentError ? (
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Payment Failed</h3>
                      <p className="mt-1 text-sm text-red-700">{paymentError}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => {
                      setPaymentError(null);
                      window.location.reload();
                    }}
                    className="w-full"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/cart')}
                    className="w-full"
                  >
                    Back to Cart
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/')}
                    className="w-full"
                  >
                    Go to Homepage
                  </Button>
                </div>
              </div>
            ) : isProcessing ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Processing payment...</p>
              </div>
            ) : showPayPal ? (
              <div id="paypal-button-container"></div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading PayPal...</p>
              </div>
            )}

            {!paymentError && (
              <div className="mt-6 text-sm text-gray-600">
                <p className="mb-2">
                  <strong>Secure Payment:</strong> Your payment information is processed
                  securely through PayPal.
                </p>
                <p>
                  <strong>Cancellation Policy:</strong> Check the listing details for
                  cancellation policies.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Extend Window interface for PayPal
declare global {
  interface Window {
    paypal?: {
      Buttons: (config: {
        createOrder: () => Promise<string>;
        onApprove: (data: { orderID: string }) => Promise<void>;
        onError: (err: Error) => void;
        style: {
          layout: string;
          color: string;
          shape: string;
          label: string;
        };
      }) => {
        render: (selector: string) => void;
      };
    };
  }
}

export default CheckoutPage;
